import { useMemo } from 'react';
// react-grid-layout v2 moved the v1-style flat-props components and
// `WidthProvider` HOC under `/legacy`. Importing from the package root
// gives you the new composable v2 API; we use the legacy entry because
// the v1 surface is what almost every published example documents.
import { Responsive, WidthProvider, type LayoutItem } from 'react-grid-layout/legacy';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import type { DashboardTile } from '@vagusx/clients';
import { TILE_REGISTRY, isKnownTileType } from './tile-registry';
import { TileFrame } from './tile-frame';

const ResponsiveGridLayout = WidthProvider(Responsive);

/**
 * Render the user's dashboard layout as an interactive grid. View
 * mode renders tiles as static cards; edit mode enables drag, resize,
 * and a per-tile remove button.
 *
 * Tiles self-load via `useDashboardMetric(metricKey)` — the grid
 * doesn't pass a bundled `stats` prop. A slow metric only blocks its
 * own tile, not siblings. Each tile mounts its own React Query;
 * shared metrics (rare today) collide on the cache key and only fetch
 * once.
 */
export function DashboardGrid({
  tiles,
  isEditing,
  period,
  dashboardCtx,
  onLayoutChange,
  onRemoveTile,
}: {
  tiles: DashboardTile[];
  isEditing: boolean;
  period: { from: string; to: string };
  dashboardCtx: { dashFrom: string; dashTo: string; dashPreset: string };
  onLayoutChange: (next: DashboardTile[]) => void;
  onRemoveTile: (id: string) => void;
}): React.ReactElement {
  const rglLayout = useMemo<LayoutItem[]>(
    () =>
      tiles.map((t) => {
        const def = isKnownTileType(t.type) ? TILE_REGISTRY[t.type] : undefined;
        return {
          i: t.id,
          x: t.x,
          y: t.y,
          w: t.w,
          h: t.h,
          minW: def?.minSize?.w ?? 2,
          minH: def?.minSize?.h ?? 2,
        };
      }),
    [tiles]
  );

  const handleLayoutChange = (next: readonly LayoutItem[]): void => {
    const byId = new Map(tiles.map((t) => [t.id, t]));
    const merged: DashboardTile[] = next
      .map((entry) => {
        const existing = byId.get(entry.i);
        if (!existing) return undefined;
        if (
          existing.x === entry.x &&
          existing.y === entry.y &&
          existing.w === entry.w &&
          existing.h === entry.h
        ) {
          return existing;
        }
        return { ...existing, x: entry.x, y: entry.y, w: entry.w, h: entry.h };
      })
      .filter((t): t is DashboardTile => t !== undefined);

    const changed = merged.some((m, i) => m !== tiles[i]);
    if (changed) onLayoutChange(merged);
  };

  return (
    <ResponsiveGridLayout
      className="dashboard-grid"
      layouts={{ lg: rglLayout }}
      breakpoints={{ lg: 0 }}
      cols={{ lg: 12 }}
      rowHeight={64}
      margin={[12, 12]}
      isDraggable={isEditing}
      isResizable={isEditing}
      draggableHandle=".tile-drag-handle"
      compactType="vertical"
      onLayoutChange={handleLayoutChange}
    >
      {tiles.map((t) => {
        const def = isKnownTileType(t.type) ? TILE_REGISTRY[t.type] : undefined;
        return (
          <div key={t.id} className="overflow-hidden">
            <TileFrame
              title={def?.title ?? 'Unknown tile'}
              isEditing={isEditing}
              onRemove={() => onRemoveTile(t.id)}
            >
              {def ? (
                def.render({
                  period,
                  config: t.config,
                  dashboardCtx,
                  // Tiles skip data fetches in edit mode — drag-to-resize
                  // re-renders shouldn't refire queries.
                  enabled: !isEditing,
                })
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Tile type &quot;{t.type}&quot; is not available in this build.
                </div>
              )}
            </TileFrame>
          </div>
        );
      })}
    </ResponsiveGridLayout>
  );
}
